import { useId, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_CONTROL_CLASS, FORM_ERROR_TEXT_CLASS, FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { parseUserCode, USER_CODE_ALPHABET, type UserCodeParseResult, type UserCode } from "./userCode";
import Field from "../../../ui/forms/Field";

interface UserCodeFormProps {
  initialText: string;
  onSubmit: (userCode: UserCode) => void;
}

/** The field for the code the connector shows. Says what is wrong before anything is sent. */
export default function UserCodeForm({ initialText, onSubmit }: UserCodeFormProps) {
  const { t } = useTranslation();
  const helpId = useId();
  const [text, setText] = useState(initialText);
  const [showProblem, setShowProblem] = useState(initialText !== "");
  const parsed = parseUserCode(text);
  const problemMessage = showProblem ? problemMessageOf(parsed) : null;
  const problem = problemMessage === null ? null : t(problemMessage.key, problemMessage.values);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (parsed.kind === "valid") {
      onSubmit(parsed.userCode);
    } else {
      setShowProblem(true);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" noValidate={true}>
      <div>
        <Field label={t("connectors.pairing.codeLabel")}>
          {(control) => (
            <input
              {...control}
              className={`${FORM_CONTROL_CLASS} font-mono text-lg tracking-widest uppercase`}
              value={text}
              onChange={(event) => {
                setText(event.target.value);
              }}
              onBlur={() => {
                setShowProblem(text.trim() !== "");
              }}
              placeholder="XXXX-XXXX"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={16}
              aria-invalid={problem !== null}
              aria-describedby={helpId}
            />
          )}
        </Field>
        <p id={helpId} className={problem === null ? FORM_HELP_TEXT_CLASS : FORM_ERROR_TEXT_CLASS}>
          {problem ?? t("connectors.pairing.codeHelp")}
        </p>
      </div>
      <AppButton type="submit" variant="primary" size="sm" disabled={parsed.kind === "empty"}>
        {t("connectors.pairing.lookUp")}
      </AppButton>
    </form>
  );
}

interface ProblemMessage {
  readonly key: string;
  readonly values: Readonly<Record<string, string | number>>;
}

function problemMessageOf(parsed: UserCodeParseResult): ProblemMessage | null {
  switch (parsed.kind) {
    case "valid":
    case "empty":
      return null;
    case "wrongLength":
      return { key: "connectors.pairing.codeWrongLength", values: { letterCount: parsed.letterCount } };
    case "invalidCharacter":
      return {
        key: "connectors.pairing.codeInvalidCharacter",
        values: { character: parsed.character, alphabet: USER_CODE_ALPHABET },
      };
  }
}
